<template>
  <footer class="legal-footer" aria-label="Footer">
    <p class="legal-footer-copy">
      m&amp;s-Sprossenelemente GmbH - alle aufgef&uuml;hrten Preise zzgl. gesetzlicher MwSt. und Versand
      <template v-if="!showProtectedLinks && impressumLink">
        <span class="legal-footer-copy-separator">|</span>
        <a
          class="legal-footer-copy-link"
          :href="impressumLink.href"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ impressumLink.label }}
        </a>
      </template>
    </p>

    <div v-if="showFeedbackButton" class="legal-footer-support">
      <button class="legal-footer-feedback-button" type="button" @click="openSupportTicketModal">
        <i class="fa-regular fa-message" aria-hidden="true"></i>
        <span>Feedback</span>
      </button>
    </div>

    <nav v-if="showProtectedLinks" class="legal-footer-links" aria-label="Rechtliche Hinweise">
      <a
        v-for="link in legalLinks"
        :key="link.label"
        :href="link.href"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ link.label }}
      </a>
    </nav>

    <nav class="legal-footer-social" aria-label="Social Media">
      <a
        v-for="link in socialLinks"
        :key="link.label"
        :href="link.href"
        target="_blank"
        rel="noopener noreferrer"
        :aria-label="link.label"
      >
        <img :src="link.icon" :alt="link.label">
      </a>
    </nav>

    <a
      class="legal-footer-brand"
      href="https://www.sander-gruppe.eu/"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="m&s Webseite"
    >
      <img :src="sanderLogo" alt="Sander-Logo">
    </a>

    <div v-if="showFeedbackButton && showSupportTicketModal" class="support-ticket-modal-overlay" @click.self="closeSupportTicketModal">
      <form class="support-ticket-modal" role="dialog" aria-modal="true" aria-labelledby="support-ticket-modal-title" aria-describedby="support-ticket-intro" @submit.prevent="submitSupportTicket">
        <div class="support-ticket-modal-header">
          <div class="support-ticket-modal-heading">
            <span class="support-ticket-modal-icon" aria-hidden="true"><i class="fa-regular fa-message"></i></span>
            <div>
              <h2 id="support-ticket-modal-title" class="support-ticket-modal-title">Feedback</h2>
              <p class="support-ticket-modal-subtitle">Ihre Meinung zum Konfigurator</p>
            </div>
          </div>
          <button type="button" class="support-ticket-modal-close" aria-label="Feedback schließen" :disabled="isSupportTicketSubmitting" @click="closeSupportTicketModal">×</button>
        </div>
        <div class="support-ticket-modal-content">
          <p id="support-ticket-intro" class="support-ticket-intro">
            Teilen Sie uns Ihre Anregung mit oder melden Sie ein Problem.
          </p>
          <label class="support-ticket-field" for="support-ticket-subject">
            <span>Betreff</span>
            <input
              id="support-ticket-subject"
              ref="supportTicketSubjectInput"
              v-model="supportTicketForm.subject"
              type="text"
              placeholder="Worum geht es?"
              maxlength="150"
              required
              :disabled="isSupportTicketSubmitting"
            />
          </label>
          <label class="support-ticket-field" for="support-ticket-message">
            <span>Text</span>
            <textarea
              id="support-ticket-message"
              v-model="supportTicketForm.message"
              rows="6"
              placeholder="Beschreiben Sie Ihre Anregung oder das Problem …"
              maxlength="5000"
              required
              :disabled="isSupportTicketSubmitting"
            ></textarea>
          </label>
          <div class="support-ticket-field">
            <span class="support-ticket-attachment-label">Screenshot <span class="support-ticket-optional">optional</span></span>
            <div class="support-ticket-attachment">
              <span class="support-ticket-attachment-icon" aria-hidden="true"><i class="fa-regular fa-image"></i></span>
              <div class="support-ticket-attachment-details">
                <span class="support-ticket-attachment-title">Ein Bild hilft uns weiter</span>
                <span class="support-ticket-attachment-hint">PNG, JPG oder GIF · max. 10 MB</span>
              </div>
              <input
                id="support-ticket-screenshot"
                ref="supportTicketScreenshotInput"
                class="support-ticket-file-input"
                type="file"
                accept="image/png,image/jpeg,image/gif"
                :disabled="isSupportTicketSubmitting"
                @change="handleSupportTicketScreenshotChange"
              />
              <label class="support-ticket-file-button" for="support-ticket-screenshot">
                <i class="fa fa-paperclip" aria-hidden="true"></i>
                <span>{{ supportTicketScreenshotLabel }}</span>
              </label>
            </div>
          </div>
          <div v-if="supportTicketError" class="support-ticket-message support-ticket-message-error" role="alert">{{ supportTicketError }}</div>
          <div v-if="supportTicketSuccess" class="support-ticket-message support-ticket-message-success" role="status">{{ supportTicketSuccess }}</div>
        </div>
        <div class="support-ticket-modal-actions">
          <button type="button" :disabled="isSupportTicketSubmitting" @click="closeSupportTicketModal">Abbrechen</button>
          <button type="submit" :disabled="isSupportTicketSubmitting">
            <i :class="isSupportTicketSubmitting ? 'fa fa-spinner fa-spin' : 'fa-regular fa-paper-plane'" aria-hidden="true"></i>
            {{ isSupportTicketSubmitting ? 'Wird gesendet...' : 'Absenden' }}
          </button>
        </div>
      </form>
    </div>
  </footer>
</template>

<script>
import api from '@/api/client';
import facebookIcon from '@/assets/footer/Icon-awesome-facebook.svg';
import instagramIcon from '@/assets/footer/Icon-awesome-instagram.svg';
import linkedinIcon from '@/assets/footer/Icon-awesome-linkedin.svg';
import xingIcon from '@/assets/footer/Icon-simple-xing.svg';
import youtubeIcon from '@/assets/footer/Icon-awesome-youtube.svg';
import whatsappIcon from '@/assets/footer/Icon-awesome-whatsapp-square.svg';
import sanderLogo from '@/assets/footer/Sander-Gruppe.png';

const LEGAL_PATHS = {
  privacy: '/legal/2026-05-20_Datenschutzerklaerung_Plattform_V1.0.pdf',
  marketing: '/legal/2026-05-20_Nutzungsvereinbarung_Marketingportal_V1.0.pdf',
  configurator: '/legal/2026-05-20_Nutzungsbedingungen_Konfigurator_V1.0.pdf',
};

const isAbsoluteUrl = (value) => /^(https?:|mailto:|tel:)/i.test(value);

const buildBackendUrl = (value) => {
  if (!value || isAbsoluteUrl(value)) {
    return value;
  }

  try {
    return new URL(value, api.defaults.baseURL || window.location.origin).toString();
  } catch {
    return value;
  }
};

export default {
  name: 'LegalFooter',
  props: {
    showProtectedLinks: {
      type: Boolean,
      default: true,
    },
    showFeedbackButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      sanderLogo,
      showSupportTicketModal: false,
      supportTicketForm: {
        subject: '',
        message: '',
        screenshot: null,
      },
      supportTicketError: '',
      supportTicketSuccess: '',
      isSupportTicketSubmitting: false,
      legalLinks: [
        {
          label: 'Impressum',
          href: process.env.VUE_APP_LEGAL_IMPRESSUM_URL || 'https://www.ms-sprossenelemente.de/impressum/',
          public: true,
        },
        {
          label: 'Datenschutz',
          href: buildBackendUrl(process.env.VUE_APP_LEGAL_PRIVACY_PDF_URL || LEGAL_PATHS.privacy),
        },
        {
          label: 'Nutzungsvereinbarung Marketingportal',
          href: buildBackendUrl(process.env.VUE_APP_LEGAL_MARKETING_TERMS_PDF_URL || LEGAL_PATHS.marketing),
        },
        {
          label: 'Nutzungsvereinbarungen Konfigurator',
          href: buildBackendUrl(process.env.VUE_APP_LEGAL_CONFIGURATOR_TERMS_PDF_URL || LEGAL_PATHS.configurator),
        },
      ],
      socialLinks: [
        {
          label: 'Facebook',
          href: 'https://www.facebook.com/msInsektenschutz',
          icon: facebookIcon,
        },
        {
          label: 'Instagram',
          href: 'https://www.instagram.com/ms_insektenschutz/',
          icon: instagramIcon,
        },
        {
          label: 'LinkedIn',
          href: 'https://de.linkedin.com/company/m-s-sprossenelemente-gmbh',
          icon: linkedinIcon,
        },
        {
          label: 'Xing',
          href: 'https://www.xing.com/pages/m-ssprossenelementegmbh-insektenschutzgitteru-vorsatzsprossen',
          icon: xingIcon,
        },
        {
          label: 'Youtube',
          href: 'https://www.youtube.com/@mssprossenelementegmbh9629/videos',
          icon: youtubeIcon,
        },
        {
          label: 'Whatsapp',
          href: 'https://wa.me/message/BWHQ35ZD2AQYJ1',
          icon: whatsappIcon,
        },
      ],
    };
  },
  computed: {
    impressumLink() {
      return this.legalLinks.find((link) => link.public);
    },
    supportTicketScreenshotLabel() {
      return this.supportTicketForm.screenshot?.name || 'Bild auswählen';
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleDocumentKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
  },
  methods: {
    handleDocumentKeyDown(event) {
      if (event.key === 'Escape' && this.showSupportTicketModal) {
        this.closeSupportTicketModal();
      }
    },
    openSupportTicketModal() {
      this.resetSupportTicketForm();
      this.showSupportTicketModal = true;
      this.$nextTick(() => {
        this.$refs.supportTicketSubjectInput?.focus();
      });
    },
    closeSupportTicketModal() {
      if (this.isSupportTicketSubmitting) {
        return;
      }

      this.showSupportTicketModal = false;
      this.resetSupportTicketForm();
    },
    resetSupportTicketForm() {
      this.supportTicketForm = {
        subject: '',
        message: '',
        screenshot: null,
      };
      this.supportTicketError = '';
      this.supportTicketSuccess = '';
      if (this.$refs.supportTicketScreenshotInput) {
        this.$refs.supportTicketScreenshotInput.value = '';
      }
    },
    handleSupportTicketScreenshotChange(event) {
      const file = event.target.files?.[0] || null;
      this.supportTicketError = '';
      this.supportTicketSuccess = '';

      if (!file) {
        this.supportTicketForm.screenshot = null;
        return;
      }

      const isSupportedImage = ['image/png', 'image/jpeg', 'image/gif'].includes(file.type);
      if (!isSupportedImage) {
        this.supportTicketForm.screenshot = null;
        event.target.value = '';
        this.supportTicketError = 'Bitte laden Sie ein Bild im PNG-, JPG- oder GIF-Format hoch.';
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        this.supportTicketForm.screenshot = null;
        event.target.value = '';
        this.supportTicketError = 'Der Screenshot darf maximal 10 MB groß sein.';
        return;
      }

      this.supportTicketForm.screenshot = file;
    },
    async submitSupportTicket() {
      if (this.isSupportTicketSubmitting) {
        return;
      }

      const subject = this.supportTicketForm.subject.trim();
      const message = this.supportTicketForm.message.trim();

      if (!subject || !message) {
        this.supportTicketError = 'Bitte füllen Sie Betreff und Text aus.';
        return;
      }

      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('message', message);
      if (this.supportTicketForm.screenshot) {
        formData.append('screenshot', this.supportTicketForm.screenshot);
      }

      this.isSupportTicketSubmitting = true;
      this.supportTicketError = '';
      this.supportTicketSuccess = '';

      try {
        await api.post('/api/support-ticket', formData);
        this.supportTicketSuccess = 'Vielen Dank! Ihr Feedback wurde erfolgreich gesendet.';
        window.setTimeout(() => {
          if (this.showSupportTicketModal && this.supportTicketSuccess) {
            this.closeSupportTicketModal();
          }
        }, 1400);
      } catch (error) {
        console.error('Fehler beim Senden des Support-Tickets:', error);
        this.supportTicketError = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
      } finally {
        this.isSupportTicketSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.legal-footer {
  width: min(100%, 900px);
  margin: 80px auto 0;
  padding: 0 16px 28px;
  color: #747474;
  text-align: center;
  box-sizing: border-box;
}

.legal-footer-copy {
  margin: 0 0 20px;
  font-size: 10pt;
  line-height: 1.4;
}

.legal-footer-copy-separator {
  display: inline-block;
  margin: 0 8px;
  color: #b8b8b8;
}

.legal-footer-copy-link {
  color: #5f5f5f;
  font-weight: 600;
  text-decoration: none;
}

.legal-footer-copy-link:hover,
.legal-footer-copy-link:focus-visible {
  color: #e30613;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

.legal-footer-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  align-items: center;
  justify-content: center;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto 20px;
  padding: 10px 16px;
  border-top: 1px solid #e2e2e2;
  border-bottom: 1px solid #e2e2e2;
}

.legal-footer-links a {
  color: #5f5f5f;
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.35;
  text-decoration: none;
  transition: color 0.15s ease, text-decoration-color 0.15s ease;
}

.legal-footer-links a:hover,
.legal-footer-links a:focus-visible {
  color: #e30613;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

.legal-footer-support {
  display: flex;
  justify-content: center;
  margin: 0 0 24px;
}

.legal-footer-feedback-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 140px;
  max-width: 100%;
  min-height: 42px;
  padding: 9px 18px;
  border: 1px solid #d88a91;
  border-radius: 22px;
  background: #fff8f8;
  color: #555555;
  box-shadow: none;
  cursor: pointer;
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.legal-footer-feedback-button i {
  color: #e30613;
  font-size: 1rem;
}

.legal-footer-feedback-button:hover,
.legal-footer-feedback-button:focus-visible {
  border-color: #e30613;
  background: #ffefef;
  color: #b90510;
  box-shadow: 0 2px 6px rgba(227, 6, 19, 0.12);
  transform: translateY(-1px);
}

.legal-footer-feedback-button:focus-visible {
  outline: 2px solid #a20711;
  outline-offset: 3px;
}

.support-ticket-modal-overlay {
  position: fixed;
  inset: 0;
  padding: 24px;
  background: rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(6px) brightness(0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.support-ticket-modal {
  width: min(640px, 100%);
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  background: #ffffff;
  border: 1px solid #e0dede;
  border-radius: 10px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #4d4d4d;
  text-align: left;
}

.support-ticket-modal-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid #ededed;
  background: #ffffff;
}

.support-ticket-modal-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.support-ticket-modal-icon {
  display: grid;
  place-items: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 1px solid #f0d7d9;
  border-radius: 8px;
  background: #fff8f8;
  color: #e30613;
  font-size: 18px;
}

.support-ticket-modal-title {
  margin: 0;
  color: #4d4d4d;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.25;
}

.support-ticket-modal-subtitle {
  margin: 4px 0 0;
  color: #8a8a8a;
  font-size: 12px;
  line-height: 1.4;
}

.support-ticket-modal-close {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #747474;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.support-ticket-modal-close:not(:disabled):hover {
  border-color: #e0dede;
  background: #f6f6f6;
  color: #333333;
}

.support-ticket-modal-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  text-align: left;
}

.support-ticket-intro {
  margin: 0;
  color: #747474;
  font-size: 14px;
  line-height: 1.55;
}

.support-ticket-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  color: #666666;
  font-size: 13px;
  font-weight: 600;
}

.support-ticket-field input[type="text"],
.support-ticket-field textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e0dede;
  border-radius: 6px;
  background: #fafafa;
  color: #4d4d4d;
  font: 400 14px/1.5 Arial, sans-serif;
  padding: 10px 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.support-ticket-field input[type="text"] {
  min-height: 42px;
}

.support-ticket-field input::placeholder,
.support-ticket-field textarea::placeholder {
  color: #9a9a9a;
}

.support-ticket-field textarea {
  min-height: 150px;
  max-height: 280px;
  resize: vertical;
}

.support-ticket-field input[type="text"]:focus,
.support-ticket-field textarea:focus {
  outline: none;
  border-color: #e30613;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(227, 6, 19, 0.08);
}

.support-ticket-attachment-label {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.support-ticket-optional {
  color: #999999;
  font-size: 11px;
  font-weight: 400;
}

.support-ticket-attachment {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px dashed #d8d8d8;
  border-radius: 7px;
  background: #fafafa;
}

.support-ticket-attachment-icon {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: #eeeeee;
  color: #8a8a8a;
  font-size: 17px;
}

.support-ticket-attachment-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.support-ticket-attachment-title {
  color: #666666;
  font-size: 12px;
  font-weight: 500;
}

.support-ticket-attachment-hint {
  color: #999999;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.4;
}

.support-ticket-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.support-ticket-file-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: fit-content;
  max-width: 210px;
  min-height: 36px;
  box-sizing: border-box;
  padding: 8px 12px;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  background: #ffffff;
  color: #747474;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.support-ticket-file-button span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.support-ticket-file-button:hover {
  border-color: #b5b5b5;
  background-color: #f6f6f6;
  color: #333333;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.support-ticket-file-input:focus-visible + .support-ticket-file-button,
.support-ticket-modal-close:focus-visible,
.support-ticket-modal-actions button:focus-visible {
  outline: 2px solid #a20711;
  outline-offset: 3px;
}

.support-ticket-file-input:disabled + .support-ticket-file-button {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.support-ticket-message {
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
}

.support-ticket-message-error {
  border: 1px solid #e30613;
  background-color: #fff5f5;
  color: #a9040d;
}

.support-ticket-message-success {
  border: 1px solid #3c9d5d;
  background-color: #f3fbf6;
  color: #24633c;
}

.support-ticket-modal-actions {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #ededed;
  background: #fafafa;
}

.support-ticket-modal-actions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 112px;
  min-height: 40px;
  padding: 10px 16px;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  background: #ffffff;
  color: #747474;
  font: 600 13px/1.2 Arial, sans-serif;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.support-ticket-modal-actions button[type="submit"] {
  border-color: #e30613;
  background: #e30613;
  color: #ffffff;
}

.support-ticket-modal-actions button:not(:disabled):hover {
  background-color: #f6f6f6;
  color: #333333;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.support-ticket-modal-actions button[type="submit"]:not(:disabled):hover {
  border-color: #a9040d;
  background-color: #a9040d;
  color: #ffffff;
}

.support-ticket-modal-actions button:disabled,
.support-ticket-modal-close:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.legal-footer-social {
  display: flex;
  gap: 11px;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.legal-footer-social a,
.legal-footer-brand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.legal-footer-social img {
  width: 1.5rem;
  max-width: 1.5rem;
  height: auto;
  transition: transform 0.15s ease, opacity 0.15s ease;
}

.legal-footer-social a:hover img,
.legal-footer-social a:focus-visible img,
.legal-footer-brand:hover img,
.legal-footer-brand:focus-visible img {
  opacity: 0.8;
  transform: translateY(-1px);
}

.legal-footer-brand img {
  width: 3.6rem;
  max-width: 3.6rem;
  height: auto;
}

@media (max-width: 640px) {
  .legal-footer {
    margin-top: 48px;
    padding-inline: 12px;
  }

  .legal-footer-links {
    gap: 7px 12px;
    padding-inline: 10px;
  }

  .legal-footer-links a {
    font-size: 0.8rem;
  }

  .legal-footer-support {
    margin-bottom: 18px;
  }

  .support-ticket-modal-overlay {
    padding: 12px;
  }

  .support-ticket-modal {
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
  }

  .support-ticket-modal-header {
    padding: 16px 18px;
  }

  .support-ticket-modal-content {
    padding: 18px;
  }

  .support-ticket-field textarea {
    min-height: 140px;
  }

  .support-ticket-attachment {
    grid-template-columns: 36px minmax(0, 1fr);
  }

  .support-ticket-file-button {
    grid-column: 1 / -1;
    max-width: 100%;
    justify-self: start;
  }

  .support-ticket-modal-actions {
    padding: 14px 18px;
  }

  .support-ticket-modal-actions button {
    flex: 1;
    min-width: 0;
  }
}
</style>
